import {
  toCamelCase,
  WSButton,
  WSButtons,
  WSElement,
  WSFormOld,
  WSPanel,
  WSRadioInputGroup,
  WSSidebar,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import styles from "./styles.module.scss";
import {
  AttributeDataType,
  CustomField,
  CustomFieldResourceType
} from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";
import { WSQueries } from "../../../../query/WSQuery";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { queriesCustomFields } from "@query";
import { getParentPath } from "../../../../utils/goToParentRoute";
import {
  CustomFieldResourceTypeRouteSlug,
  ResourceTypeSlugToResourceType
} from "../../utils";

export const RouteCustomFieldDetails: React.FC<
  RouteComponentProps<{
    customFieldId: string;
    resourceTypeSlug: CustomFieldResourceTypeRouteSlug;
  }>
> = ({ match }) => {
  const history = useHistory();
  const location = useLocation<{ backPath: string }>();
  const backPath = location.state?.backPath || getParentPath(history);
  const queryCustomField = queriesCustomFields.getCustomField.useQuery(
    { fieldId: match.params.customFieldId },
    { refetchOnMount: false }
  );

  const onBack = () => {
    if (backPath) {
      history.push({
        pathname: backPath,
        search: location.search,
        state: {
          ...(location.state ?? {})
        }
      });
    }
  };

  return (
    <WSSidebar.Container onClose={onBack}>
      <WSQueries queries={{ queryCustomField }}>
        {({ queryCustomFieldData: customField }) => (
          <WSSidebar.Layout header={customField.name}>
            <WSPanel noBorder>
              <Inner
                data={customField}
                resourceType={
                  ResourceTypeSlugToResourceType[match.params.resourceTypeSlug]
                }
                onBack={onBack}
              />
            </WSPanel>
          </WSSidebar.Layout>
        )}
      </WSQueries>
    </WSSidebar.Container>
  );
};

export const Inner: React.FC<{
  data: CustomField;
  resourceType: CustomFieldResourceType;
  onBack: () => void;
}> = ({ data, resourceType, onBack }) => {
  const queryCustomFieldList = queriesCustomFields.listCustomFields.useQueryAll(
    { filter: { resourceType: { eq: resourceType } } }
  );
  const fieldId = data.id ?? "";

  const [updateCustomField, updateCustomFieldMeta] =
    queriesCustomFields.updateCustomField.useMutation();
  const [deleteCustomField, deleteCustomFieldMeta] =
    queriesCustomFields.deleteCustomField.useMutation();

  return (
    <>
      <WSFormOld
        defaultValues={{
          name: data.name ?? "",
          fieldType: data.dataType ?? AttributeDataType.STRING,
          required: data.isRequired ? "yes" : "no"
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .notOneOf(
              queryCustomFieldList.flatData
                .map(customField => customField.name)
                .filter(name => name !== data.name),
              "A custom field with this name already exists"
            )
            .required("Name is required"),
          fieldType: Yup.string(),
          required: Yup.string()
        })}
        onSubmit={async formData => {
          await updateCustomField(
            {
              fieldId,
              customFieldUpdate: {
                name: formData.name,
                isRequired: formData.required === "yes",
                dataType: formData.fieldType,
                key: toCamelCase(formData.name)
              }
            },
            {
              onSuccess() {
                onBack();
              }
            }
          );
        }}
      >
        <WSFormOld.Field
          mb="XL"
          label="Field name"
          name="name"
          component={WSTextInput}
        />

        <WSFormOld.Field
          mb="2XL"
          label=" "
          name="required"
          component={WSRadioInputGroup}
          componentProps={{
            options: [
              {
                label: "Required",
                value: "yes"
              },
              {
                label: "Optional",
                value: "no"
              }
            ]
          }}
        />

        <WSElement className={styles.disabled}>
          <WSFormOld.Label mb="M">Field type</WSFormOld.Label>
          <WSRadioInputGroup
            value={data.dataType}
            options={[
              {
                label: "Text field",
                value: AttributeDataType.STRING
              },
              {
                label: "Checkbox",
                value: AttributeDataType.BOOLEAN
              }
            ]}
            name="disabled"
          />
          <WSText.ParagraphSm mt="M" color="gray500">
            Field type cannot be edited for existing fields.
          </WSText.ParagraphSm>
        </WSElement>

        <ErrorAlert
          mt="XL"
          error={updateCustomFieldMeta.error || deleteCustomFieldMeta.error}
          context="Other"
        />

        <WSButtons mt="3XL" forceFullWidth>
          <WSFormOld.SubmitButton data-testid="submit">
            Update field
          </WSFormOld.SubmitButton>
          <WSButton
            destructive
            type="button"
            onAsyncClick={async () => {
              await deleteCustomField({ fieldId });
              onBack();
            }}
          >
            Delete field
          </WSButton>
        </WSButtons>
      </WSFormOld>
    </>
  );
};
